import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import dynamic from "next/dynamic";
import { getColor } from "@gc-npm/utils.enums.colors";
import TextInfo from "@gc-npm/components.text";
import CustomLink from "@gc-npm/components.custom-link";
import CardTemplate from "@/component/cardBanner/card-template";
import PostCard from "@/component/cardBanner/post-card";

const Image = dynamic(() => import("@/component/c-image"));

const hasInteractiveContent = (value) => {
  if (typeof value === "string") {
    return /<\s*(?:a|button|input|select|textarea|summary|iframe|object|embed|audio|video|label)\b|\b(?:tabindex|contenteditable|on[a-z]+)\s*=/i.test(value);
  }
  if (!value || typeof value !== "object") return typeof value === "function";
  return Object.entries(value).some(([key, item]) => {
    if (["href", "onClick", "onKeyDown", "tabIndex", "function"].includes(key) && item !== undefined && item !== null && item !== "") return true;
    return hasInteractiveContent(item);
  });
};

const canWrapContent = (props) => {
  const link = props["CustomLink-1"];
  if (props.children || typeof link?.href !== "string" || !/^(?:https?:\/\/|\/(?!\/)|#|\?)/i.test(link.href)) return false;
  if (link.cmsHandler || link.cms || link.buttonParent || link.disabled || link.handledBySa2m || link.screenReadable === false || (link.accessibleRole && link.accessibleRole !== "link")) return false;
  if (props["Glyph-2"]?.src || Object.keys(props["Characteristics-1"] || {}).length) return false;
  return !["Image-1", "Image-2", "Image-3", "Glyph-1", "Label-0", "Label-1", "Label-2", "Price-1", "Price-2", ...Array.from({ length: 6 }, (_, index) => `Text-${index + 1}`)].some(key => hasInteractiveContent(props[key]));
};

const extraerTextoSpan = (cadena) => {
  const patron = /<span[^>]*>(.*?)<\/span>/;
  const elementoEncapsulado = patron.test(cadena);
  if (elementoEncapsulado) {
    const resultado = cadena.match(patron);
    if (resultado) {
      return resultado[1];
    } else {
      null;
    }
  }
  return cadena;
};

const comprobarComponentVariation = (taggingInfo, link) => {
  if (taggingInfo && taggingInfo.componentVariation) {
    return taggingInfo.componentVariation;
  } else if (link && link.componentVariation) {
    return link.componentVariation;
  }
  return "all";
};

const Card = (restProps) => {
  const {
    layout,
    backgroundColor,
    skeletonClassName,
    className,
    cardShadow,
    backgroundOpacity,
    setPriority,
    children,
    hash,
    imageAspectRatio,
  } = restProps;
  const titleCusto = restProps["Text-2"]?.text;
  const link = restProps["CustomLink-1"]?.text;
  const backgroundImg = restProps["Image-1"];
  const primaryLink = restProps["CustomLink-1"];
  const wrapContent = canWrapContent(restProps);
  const CardElement = wrapContent ? CustomLink : "div";
  let finalClassName = className;
  // const imgBackground = {
  //   desktop: restProps["Image-1"],
  //   tablet: restProps["Image-tablet"],
  //   mobile: restProps["Image-mobile"],
  // };

  const taggingInfo = restProps.taggingInfo && {
    ...restProps.taggingInfo,
    componentVariation: comprobarComponentVariation(
      restProps.taggingInfo,
      link
    ),
    componentName: "banner-azul-listado",
    componentContent: extraerTextoSpan(titleCusto),
    eventType: "click",
  };

  const computeBackgroundColor = (color) => {
    if (color == "") {
      color = "transparent";
    }
    if (color && typeof color === "string") {
      if (color.charAt(0) === "#") {
        return color;
      } else {
        return getColor(color);
      }
    }
    return getColor(color);
  };

  const cardStyle = {
    backgroundColor: computeBackgroundColor(backgroundColor),
    height: "100%",
  };
  if (cardShadow) {
    finalClassName =
      finalClassName + ` card-shadow--${layout === "dispo-product" ? 1 : 10}`;
  }

  return (
    <div className="card-column" style={{ height: "100%" }}>
      {restProps["Text-0"] && restProps["Text-0"].text && (
        <div className="card-title">
          <TextInfo {...restProps["Text-0"]} cydata="Card TV Text-0" />
        </div>
      )}
      <CardElement
        {...(wrapContent ? { ...primaryLink, ariaLabel: undefined, title: primaryLink.title || "", taggingInfo, cydata: "Card CustomLink-1" } : {})}
        className={`card layout-${layout} ${finalClassName}${wrapContent ? " card-clickable card-banner-link" : ""}`}
        style={cardStyle}
      >
        <>
          {backgroundImg && backgroundImg.src && (
            <div className={`card-backgroundImg-1`}>
              <Image
                hash={hash}
                {...backgroundImg}
                alt={backgroundImg.alt || ""}
                fill
                objectFit={backgroundImg.objectFit || "cover"}
                cydata={`Card Image-1 ${backgroundImg.alt}`}
              />
              {!!backgroundOpacity && <div className="card-img--opacity"></div>}
            </div>
          )}
          <div className="card-container">
            {children && children}
            {!children && <CardTemplate {...restProps} />}
          </div>
          {!wrapContent && restProps["CustomLink-1"] &&
            (restProps["CustomLink-1"].href ||
              restProps["CustomLink-1"].onClick) && (
              <CustomLink
                {...restProps["CustomLink-1"]}
                title={restProps["CustomLink-1"].title || ""}
                taggingInfo={taggingInfo}
                className="card-clickable"
                cydata="Card CustomLink-1"
              />
            )}
        </>

        <div className={skeletonClassName} />
        {wrapContent && primaryLink.ariaLabel && <span className="card-banner-link-description">{primaryLink.ariaLabel}</span>}
      </CardElement>

      {/* Post Card Content Mobile */}
      {restProps["Text-7"] && restProps["Text-7"].text && (
        <PostCard
          {...restProps["Text-7"]}
          skeletonClassName={skeletonClassName}
        />
      )}

      <style jsx>
        {`
          .card-title {
            margin-bottom: 1rem;
          }
          :global(.card-banner-link) {
            color: inherit;
            text-decoration: none;
          }
          :global(.card-banner-link:focus-visible) {
            outline: 2px solid ${getColor("movistarblue")};
            outline-offset: 3px;
          }
          .card-banner-link-description {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
          }
        `}
      </style>
    </div>
  );
};

Card.propTypes = {
  layout: PropTypes.string,
  backgroundColor: PropTypes.string,
  className: PropTypes.string,
};

Card.defaultProps = {
  layout: "capta",
  backgroundColor: "transparent",
  className: "",
};

export default Card;
